import React, { useContext, useEffect, useState } from 'react';
import Currency from 'react-currency-formatter';
import { WardrobesConfigsContext } from '../../contexts/WardrobesConfigsContext';
import { ActiveWardrobeContext } from '../../contexts/ActiveWardrobeContext';
import calculatePrice from '../../helpers/calculatePrice';
import SummaryProductRow from '../SummaryList/SummaryProductRow';
import { connect } from 'react-redux';
import { prepareFrontsList, prepareHandlesList } from '../../helpers/wardrobe';
import { checkFreeAccessories } from '../../helpers/wardrobe';
import fixCornerWardrobeDepth from 'helpers/fixCornerWardrobeDepth';

const SummaryTitle = ({ title, value, regularValue, type = 1 }) => (
  <header className="summary-title">
    <img
      className="summary-title__ico"
      src={`${process.env.PUBLIC_URL}/img/door_type_${type}.png`}
      alt="szafa"
    />
    <h2 className="summary-title__name">{title}</h2>
    <h2 className="summary-title__price">
      <del>{value < regularValue && <Currency quantity={regularValue} currency="PLN" />}</del>
      <Currency quantity={value} currency="PLN" />{' '}
    </h2>
  </header>
);

const AccessoriesItem = accessory => {
  return (
    <div className="text-summary__row" key={accessory.uId}>
      <p className="text-summary__paragraph text-summary__paragraph--width">{accessory.name}</p>
      <p></p>
      {accessory.price.actual < accessory.price.regular && (
        <p className="text-summary__price text-summary__price--decoration">
          <Currency quantity={accessory.price.regular} currency="PLN" />
        </p>
      )}
      <p className="text-summary__price">
        {accessory.gratis ? (
          <span>GRATIS</span>
        ) : (
          <Currency quantity={accessory.price.actual} currency="PLN" />
        )}
      </p>
    </div>
  );
};

const prepareWardrobeName = product => {
  const dimensions = Object.values(product.dimensions).join('/');
  if (product.is_corner) {
    return (
      `Szafa Flex narożna ` +
      fixCornerWardrobeDepth(product, 'Narożnik 66x45', product.dimensions.x, 70) +
      '/' +
      product.dimensions.y +
      '/' +
      fixCornerWardrobeDepth(product, 'Narożnik 45x66', product.dimensions.z, 70)
    );
  } else {
    return `Szafa Flex ${dimensions}`;
  }
};

const WardrobeSummaryPopupContent = ({
  frontPatterns,
  setOpenDoorsState,
  setHighlightedWardrobe,
}) => {
  const { wardrobesConfigs } = useContext(WardrobesConfigsContext);
  const { activeWardrobe } = useContext(ActiveWardrobeContext);

  const product = wardrobesConfigs[activeWardrobe] || {};

  const [summaryState, setSummaryState] = useState({
    hasBrake: false,
    hasFronts: false,
    hasTrack: false,
    hasAddons: false,
  });
  const [didSet, setDidSet] = useState(false);
  const [wardrobeCopy, setWardrobeCopy] = useState('');

  useEffect(() => {
    if (Object.prototype.hasOwnProperty.call(product, 'addons')) {
      const hasBrake = product?.addons.hasOwnProperty('brake') && !!product.addons.brake;
      const hasExternalLight =
        product?.addons.hasOwnProperty('external_light') && !!product.addons.external_light;
      const hasFronts =
        product?.addons.hasOwnProperty('fronts') && !!product.addons?.fronts?.length;
      const hasTrack = product?.addons.hasOwnProperty('track') && !!product.addons.track;

      setSummaryState({
        hasBrake,
        hasFronts,
        hasTrack,
        hasExternalLight,
        hasAddons: hasBrake || hasFronts || hasTrack,
      });
    }
  }, [product]);

  const getImageFromCanvas = () => {
    return new Promise(resolve => {
      setHighlightedWardrobe(null);
      setOpenDoorsState(false);

      setTimeout(() => {
        const canvasRenderer = window.brwRenderer;
        canvasRenderer.render(window.brwScene, window.brwCamera);
        resolve(canvasRenderer.domElement.toDataURL());
      }, 100);
    });
  };

  const setImage = () => {
    setTimeout(() => {
      getImageFromCanvas().then(src => {
        setWardrobeCopy(src);
      });
    }, 100);
  };
  const popup = document.querySelector(`[data-popup-name="summary-wardrobe-popup"]`);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let timeout;
    if (!didSet && window.brwScene.visible && popup && popup.classList.contains('open')) {
      timeout = setTimeout(() => {
        setImage();
        setDidSet(true);
      }, 200);
    }
    if (popup && !popup.classList.contains('open') && didSet) {
      setDidSet(false);
    }
    if (didSet) {
      clearTimeout(timeout);
    }
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  if (!product) {
    return null;
  }

  const frontsList = prepareFrontsList(product, frontPatterns);

  const { overall, accessories } = calculatePrice(product);
  const freeAccessories = checkFreeAccessories(accessories);

  const handlesList = prepareHandlesList(product);

  const wardrobeName = prepareWardrobeName(product);

  const wardrobeType = product.is_hinged ? 1 : 2;

  return (
    <div className="pop-up__content">
      {product !== undefined && product?.hasOwnProperty('safo') && (
        <>
          <div className="wrapper">
            <SummaryTitle
              title={wardrobeName}
              value={overall.actual}
              regularValue={overall.regular}
              type={wardrobeType}
            />
            {!!wardrobeCopy && (
              <img className="wardrobe-copy-image" src={wardrobeCopy} alt={wardrobeName} />
            )}
            <div className="text-summary__dimensions">
              <h3 className="text-summary__small-title">Wymiary szafy:</h3>
              <p className="text-summary__dimensions-size-wrapper">
                {fixCornerWardrobeDepth(product, 'Narożnik 66x45', 'Głębokość:', 'Szerokość:')}{' '}
                <span className="text-summary__dimensions-size">
                  {fixCornerWardrobeDepth(product, 'Narożnik 45x66', product.dimensions.z, 70)}
                  &nbsp;cm
                </span>
              </p>
              <p className="text-summary__dimensions-size-wrapper">
                Wysokość:{' '}
                <span className="text-summary__dimensions-size">
                  {product.dimensions.y}&nbsp;cm
                </span>
              </p>
              <p className="text-summary__dimensions-size-wrapper">
                {fixCornerWardrobeDepth(product, 'Narożnik 66x45', 'Szerokość:', 'Głębokość:')}{' '}
                <span className="text-summary__dimensions-size">
                  {fixCornerWardrobeDepth(product, 'Narożnik 66x45', product.dimensions.x, 70)}
                  &nbsp;cm
                </span>
              </p>
            </div>
          </div>
          <div className="text-summary__section" style={{ marginBottom: 30 }}>
            <h3 className="text-summary__small-title text-summary__small-title--space-top">
              Elementy szafy:
            </h3>
            <h3 className="text-summary__small-title">Korpus</h3>
            <div className="text-summary__item">
              <div className="text-summary__row">
                <p className="text-summary__paragraph">Kolor korpusu</p>
                <p className="text-summary__paragraph">{product.name}</p>
                {product.price.actual < product.price.regular && (
                  <p className="text-summary__price text-summary__price--decoration">
                    <Currency quantity={product.price.regular} currency="PLN" />
                  </p>
                )}
                <p className="text-summary__price">
                  <Currency quantity={product.price.actual} currency="PLN" />
                </p>
              </div>
            </div>
            {!product.withoutDoor && (
              <>
                <h3 className="text-summary__small-title">Drzwi</h3>
                <div className="text-summary__item">
                  {frontsList &&
                    frontsList.map(item => {
                      const qty = product.is_hinged ? 1 : item.qty;
                      return (
                        item &&
                        item?.hasOwnProperty('safo') && (
                          <SummaryProductRow
                            key={Math.random()}
                            product={item}
                            qty={qty}
                            type="fronts"
                          />
                        )
                      );
                    })}
                  {handlesList &&
                    handlesList.map(
                      item =>
                        item && (
                          <SummaryProductRow key={Math.random()} product={item} type="handles" />
                        ),
                    )}
                  {summaryState.hasTrack && !!product.addons.track && (
                    <SummaryProductRow product={product.addons.track} type="track" />
                  )}
                </div>
              </>
            )}
          </div>
          <h3 className="text-summary__small-title">Akcesoria</h3>
          <div className="text-summary__item" style={{ marginBottom: 30 }}>
            {product.accessories &&
              Object.values(product.accessories).map(side => side.map(AccessoriesItem))}
            {freeAccessories.map(AccessoriesItem)}
          </div>
          {summaryState.hasAddons && (
            <div className="text-summary__item">
              <div className="text-summary__row">
                <h3 className="text-summary__small-title">Dodatki</h3>
              </div>
              {summaryState.hasBrake && !!product.addons.brake && (
                <SummaryProductRow product={product.addons.brake} type="addons" />
              )}
              {summaryState.hasExternalLight && !!product.addons.external_light && (
                <SummaryProductRow product={product.addons.external_light} type="addons" />
              )}
              {summaryState.hasFronts &&
                !product.withoutDoor &&
                !!product.addons.fronts &&
                Object.values(product.addons.fronts).map(item => (
                  <SummaryProductRow
                    key={item.name}
                    product={item}
                    qty={2}
                    multiple={true}
                    type="addons"
                  />
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const mapStateToProps = state => {
  return {
    frontPatterns: state.defaults.frontPatterns,
  };
};

export default connect(mapStateToProps)(WardrobeSummaryPopupContent);
